import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { type SearchQueryPerformanceV4Row } from "./api/v4SearchQueryPerformanceReportByQuery";
import { useQuery } from "@tanstack/react-query";
import { createColumnBuilder } from "../../components/table/columnBuilder";
import { css } from "../../../styled-system/css";
import KeywordCell from "../../components/custom/category/advertisements/KeywordCell";
import moment from "moment";
import MetricsCard from "../../components/custom/merchandise/MetricsCard";
import { useStandardEntityMetrics } from "../merchandising/components/tables/metricUtils";
import { usePersistedTableState } from "../../components/table/usePersistedTableState";
import { idsToVisibilityState } from "../../components/table/utils";
import getMetricSpec from "../merchandising/utils/metrics";
import { useGetV4SearchQueryPerformanceTotals } from "./api/v4SearchQueryPerformanceReportTotals";
import SearchQueryPerformanceDashboardTable from "./SearchQueryPerformanceDashboardTable";
import { useDates } from "../../dates/DatesContext";
import { useDateFormatter } from "../../i18n/useDateFormatter";
import { useGetV4SearchQueryReportOptions } from "./api/v4SearchQueryPerformanceOptions";
import { Flex } from "../../../styled-system/jsx";
import Loading from "../../components/core/blocks/Loading";
import { FaTriangleExclamation } from "react-icons/fa6";

const columnHelper = createColumnBuilder<SearchQueryPerformanceV4Row>();

const SearchQueryPerformanceProductSection = ({
  asin,
}: {
  asin: {
    asin: string;
    product_id: string;
  };
}) => {
  const { t } = useTranslation("sqpr");
  const { start, end } = useDates();
  const longDate = useDateFormatter("long");

  const standardMetricIds = useStandardEntityMetrics("searchQueryReport");

  const columns = useMemo(
    () => [
      columnHelper.accessor("search_query", {
        id: "search_query",
        header: t(($) => $.sqpr["Search Query"]),
        enableHiding: false,
        minSize: 100,
        size: 300,
        cell: ({ getValue }) => {
          return (
            <div className={css({ paddingLeft: "small" })}>
              <KeywordCell value={getValue()} columnWidth={300} />
            </div>
          );
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds, t],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "search_query",
    "search_query_score",
    "search_query_volume",
    "impressions_total",
    "impressions_brand_or_asin",
    "impressions_brand_or_asin_percent",
    "clicks_total",
    "cart_adds_brand_or_asin",
    "cart_adds_same_day_shipping_speed",
    "cart_adds_1d_shipping_speed",
    "cart_adds_2d_shipping_speed",
    "clicks_brand_or_asin",
    "clicks_same_day_shipping_speed",
    "clicks_1d_shipping_speed",
    "clicks_2d_shipping_speed",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["search_query"],
      end: [],
    },
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `searchQueryPerformanceProductSection`,
    initialState: initialStateConfig,
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const filters = useMemo(() => {
    const asinFilter = [
      {
        id: "asins",
        value: { op: "anyof", value: [asin.asin] },
      },
    ];

    return [...state.columnFilters, ...asinFilter];
  }, [state.columnFilters, asin]);

  const totalOptionsGetter = useGetV4SearchQueryPerformanceTotals();
  const searchQueryPerformanceTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters,
      search: debounceSearch,
    }),
  );
  const searchQueryReportOptionsQuery = useQuery(useGetV4SearchQueryReportOptions());

  const showNoDataWarning = useMemo(() => {
    if (searchQueryReportOptionsQuery.data?.data) {
      return !searchQueryReportOptionsQuery.data.data.asin.includes(asin.asin);
    }

    return false;
  }, [searchQueryReportOptionsQuery.data?.data, asin.asin]);

  const metrics = useMemo(() => {
    return standardMetricIds.map((metricId) => getMetricSpec(metricId));
  }, [standardMetricIds]);

  return (
    <PageContainer>
      {searchQueryReportOptionsQuery.isLoading ?
        <Loading fullPage={false} height={"300px"} />
      : showNoDataWarning ?
        <Flex
          className={css({
            gap: "xsmall",
            justifyContent: "center",
            alignItems: "center",
            padding: "large",
          })}
        >
          <FaTriangleExclamation />{" "}
          {t(
            ($) =>
              $.sqpr[
                "No Search Query Performance (SQPR) data is available for this ASIN. SQPR data is collected only for best-selling products."
              ],
          )}
        </Flex>
      : <MetricsCard
          id={"searchQueryPerformanceReport"}
          title={t(($) => $.sqpr["Performance Overview"])}
          totals={{
            data: searchQueryPerformanceTotalsReportQuery.data?.data,
            loading:
              searchQueryPerformanceTotalsReportQuery.isFetching &&
              !searchQueryPerformanceTotalsReportQuery.isPlaceholderData,
          }}
          metrics={metrics}
          showMetricsSelector
          initialSelectedMetric={["search_query_score", "search_query_volume"]}
          group={"day"}
          showAnnotations={true}
          byline={
            <>
              {longDate.format(moment(start).toDate())}
              {" - "}
              {longDate.format(moment(end).toDate())}
            </>
          }
          context={"searchQueryReport"}
        >
          <SearchQueryPerformanceDashboardTable
            state={state}
            initialState={initialState}
            setters={setters}
            selectedAsins={[asin]}
            renderAsinsFilter={false}
            renderExpander={false}
            columns={columns}
          />
        </MetricsCard>
      }
    </PageContainer>
  );
};

export default SearchQueryPerformanceProductSection;
